/* Rupee Xpert — cinematic DARK marketing site (`/website`, `/apply`) + legal pages.
   Full black canvas · blue+orange glow · glassmorphism · 3D-style tiles.
   All scoped under `.rx-marketing` — cannot leak into CRM styles. */

import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, PhoneCall, Wallet, BadgeCheck,
  Star, ChevronRight, ChevronLeft, FileCheck, Search, CheckCircle2,
  Banknote, Gift, Users, Building2, Home, Car, GraduationCap,
  RefreshCw, HeartHandshake, TrendingUp, Smartphone, MessageCircle,
  Zap, Brain, LayoutDashboard, Headphones, Percent, PlayCircle,
} from "lucide-react";
import { http } from "../../api";
import "./marketing.css";

// Final approved Rupee Xpert wordmark (horizontal lockup — icon + "Rupee Xpert" text).
// Used in the /apply compact header and the footer.
const LOGO_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_dsa-connect-9/artifacts/1a7sjx5l_0858390A-9FD2-4B70-BD73-A3093FEEC061.webp";
// Standalone Rupee Xpert app-tile icon (white rounded patch with blue+orange glow).
// Used only in the /website header — the site header renders the icon + a separate
// HTML wordmark so the surrounding dark rectangle of the wordmark JPEG never shows.
const ICON_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_dsa-connect-9/artifacts/mehcz9jv_IMG_8544.jpeg";

// Premium stock imagery (Unsplash — hotlink-safe, WebP-served) ------
const IMG = {
  // Approved single-image hero (self-contained composition). Two invisible
  // hotspots handle the CTA clicks. Fitted to one viewport via CSS.
  heroFigure:  "https://customer-assets-39nsmqrw.emergentagent.net/job_dsa-connect-9/artifacts/131sajdz_IMG_8609.jpeg",
  salaried:    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=1000&q=70",
  selfEmp:     "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1000&q=70",
  business:    "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1000&q=70",
  professional:"https://images.unsplash.com/photo-1584467735815-f778f274e296?auto=format&fit=crop&w=1000&q=70",
  family:      "https://images.unsplash.com/photo-1609220136736-443140cffec6?auto=format&fit=crop&w=1000&q=70",
};

const NAV = [
  { label: "Products",     href: "#products" },
  { label: "Calculators",  href: "#calculator" },
  { label: "Designed for", href: "#personas" },
  { label: "Ecosystem",    href: "#ecosystem" },
  { label: "FAQs",         href: "#faqs" },
];

const PARTNER_BANKS = [
  "HDFC Bank", "ICICI Bank", "Axis Bank", "Kotak Mahindra",
  "IndusInd", "Yes Bank", "IDFC First", "Bajaj Finserv",
  "Tata Capital", "Aditya Birla", "Fullerton", "Piramal Finance",
  "L&T Finance", "Federal Bank", "RBL Bank", "DCB Bank",
];

const PRODUCTS = [
  { id: "personal", Icon: Users,          label: "Personal Loan",         rate: "10.49%", tone: "orange", maxLabel: "Up to ₹75 Lakh" },
  { id: "business", Icon: Building2,      label: "Business Loan",         rate: "13.99%", tone: "blue",   maxLabel: "Up to ₹2.5 Crore" },
  { id: "bt",       Icon: RefreshCw,      label: "Balance Transfer",      rate: "9.99%",  tone: "orange", maxLabel: "Up to ₹75 Lakh" },
  { id: "home",     Icon: Home,           label: "Home Loan",             rate: "8.35%",  tone: "blue",   maxLabel: "Up to ₹5 Crore" },
  { id: "lap",      Icon: HeartHandshake, label: "Loan Against Property", rate: "9.25%",  tone: "orange", maxLabel: "Up to ₹5 Crore" },
  { id: "auto",     Icon: Car,            label: "Auto Loan",             rate: "9.10%",  tone: "blue",   maxLabel: "Up to ₹3 Crore" },
  { id: "edu",      Icon: GraduationCap,  label: "Education Loan",        rate: "9.50%",  tone: "orange", maxLabel: "Up to ₹3.5 Crore" },
];

const PERSONAS = [
  { id: "salaried",     label: "Salaried",       image: IMG.salaried,
    blurb: "Instant eligibility check with 20+ banks. Digital sanction possible." },
  { id: "self",         label: "Self-Employed",  image: IMG.selfEmp,
    blurb: "Cash-flow led credit — audited across NBFCs that get your business." },
  { id: "business",     label: "Business Owners", image: IMG.business,
    blurb: "Working capital, term loans and LAP for expansion moments." },
  { id: "professional", label: "Professionals",  image: IMG.professional,
    blurb: "Special schemes for CAs, doctors, engineers and consultants." },
  { id: "family",       label: "Families",       image: IMG.family,
    blurb: "Home loan, top-up and gift on disbursement — for milestone moves." },
];

const ECOSYSTEM = [
  { Icon: Smartphone,     title: "Digital Loan Journey",  body: "Apply, upload documents and track sanction from any device — one continuous timeline." },
  { Icon: MessageCircle,  title: "WhatsApp Assistance",   body: "Your advisor is one message away — status, sanction letters and reminders on chat." },
  { Icon: Zap,            title: "Fast Application",      body: "60-second lead capture. Advisor callback within two business hours, always." },
  { Icon: Brain,          title: "Smart Loan Matching",   body: "We evaluate 50+ lender programs and only surface offers you are likely to be approved for." },
  { Icon: LayoutDashboard,title: "Customer Dashboard",    body: "Every fee, every sanction letter, every disbursement — visible in one place." },
  { Icon: Headphones,     title: "Sales Support",         body: "A single named advisor from first call to disbursement. No call-centre queues." },
];

const FAQS = [
  { q: "How quickly will I hear back after applying?",
    a: "Our advisors typically call within 2 business hours to understand your requirement and match you with the right lender." },
  { q: "Does Rupee Xpert charge any upfront fee?",
    a: "No. Rupee Xpert never charges customers upfront for the advisory service. Any bank processing fee is disclosed clearly on the sanction letter issued by the lender." },
  { q: "Which banks and NBFCs do you work with?",
    a: "We partner with 50+ Bank & NBFC Partners across Personal, Business, Home, Auto, Education, LAP and Balance-Transfer products. Final eligibility, interest rate and offer are decided by the lender." },
  { q: "Is the free-gift offer guaranteed?",
    a: "Eligible customers receive a promotional gift only after their loan is successfully disbursed by a partner lender. Gifts, categories and stock are subject to the applicable offer terms." },
  { q: "How is my personal information used?",
    a: "Details you share are used strictly to evaluate loan eligibility with our partner lenders. We never sell or misuse customer data — see our Privacy Policy for the full policy." },
];

const JOURNEY = [
  { Icon: FileCheck,    t: "Apply in 60 seconds",    d: "Share basic details — no paperwork needed to start." },
  { Icon: Search,       t: "Document verification",  d: "A named advisor reviews your file and flags eligible lenders." },
  { Icon: TrendingUp,   t: "Loan processing",        d: "Your profile is submitted to matched banks in parallel." },
  { Icon: CheckCircle2, t: "Approval",               d: "Sanction letter shared — every fee disclosed upfront." },
  { Icon: Banknote,     t: "Disbursement",           d: "Funds are credited directly to your bank account." },
  { Icon: Gift,         t: "Free gift",              d: "Eligible customers receive a curated thank-you gift." },
];

const TESTIMONIALS = [
  { name: "R.N.", quote: "The advisor handled my balance-transfer end-to-end. My EMI dropped and I never had to visit a bank branch.", tag: "Balance Transfer · Bengaluru" },
  { name: "P.S.", quote: "Applied on a Sunday, got a call Monday morning, sanction letter on Wednesday. The rate was better than my own bank's offer.", tag: "Personal Loan · Mumbai" },
  { name: "A.M.", quote: "For our second-store working capital, they compared several lenders and picked the one that suited our seasonal cash-flow.", tag: "Business Loan · Ahmedabad" },
  { name: "S.K.", quote: "Very transparent process. Every fee was disclosed before I signed. Home-loan disbursement was faster than I expected.", tag: "Home Loan · Pune" },
];

// =================================================================
// Hooks
// =================================================================
function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }),
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

function useCounter(target, { duration = 1600, decimals = 0, start = false } = {}) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return decimals > 0 ? v.toFixed(decimals) : Math.round(v);
}

// =================================================================
// Header + Footer
// =================================================================
function Header({ compact = false }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`rx-header ${scrolled ? "is-solid" : ""}`}>
      <div className="rx-container rx-header-row">
        <a href="/website" className="rx-brand" data-testid="rx-brand">
          <img src={ICON_URL} alt="" className="rx-brand-icon" aria-hidden="true" />
          <span className="rx-brand-word-wrap">
            <span className="rx-brand-word">Rupee Xpert</span>
            <span className="rx-brand-tag">Loan Made Effortless</span>
          </span>
        </a>
        {!compact && (
          <nav className="rx-nav" aria-label="Primary">
            {NAV.map((n) => <a key={n.label} href={n.href}>{n.label}</a>)}
          </nav>
        )}
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="rx-footer">
      <div className="rx-container">
        <div className="rx-footer-grid">
          <div>
            <a href="/website" className="rx-brand">
              <img src={ICON_URL} alt="" className="rx-brand-icon rx-brand-icon-lg" aria-hidden="true" />
              <span className="rx-brand-word rx-brand-word-lg">Rupee Xpert</span>
            </a>
            <p style={{ marginTop: 14, maxWidth: 340 }}>
              Rupee Xpert is a channel partner and authorised Direct Sales Associate for regulated
              Indian banks and NBFCs. We do not lend on our own books.
            </p>
            <div className="rx-badges" aria-hidden="true">
              <span><ShieldCheck size={14} /> ISO-aligned data handling</span>
              <span><BadgeCheck size={14} /> RBI-regulated lenders</span>
            </div>
          </div>
          <div>
            <h5>Loan Products</h5>
            <ul>
              {PRODUCTS.slice(0, 6).map((p) => <li key={p.id}><a href="/website#products">{p.label}</a></li>)}
            </ul>
          </div>
          <div>
            <h5>Company</h5>
            <ul>
              <li><a href="/website#personas">Designed for you</a></li>
              <li><a href="/website#ecosystem">Ecosystem</a></li>
              <li><a href="/website#faqs">FAQs</a></li>
              <li><a href="/website#contact">Contact</a></li>
            </ul>
          </div>
          <div id="contact">
            <h5>Reach us</h5>
            <ul>
              <li>Mon – Sat · 10:00 to 19:00</li>
              <li><a href="mailto:hello@rupee-xpert.com">hello@rupee-xpert.com</a></li>
              <li><a href="tel:+911800000000">+91 1800 000 000</a></li>
            </ul>
            <h5 style={{ marginTop: 22 }}>Legal</h5>
            <ul>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms &amp; Conditions</a></li>
              <li><a href="/disclosures">Fair-Practice &amp; Disclosures</a></li>
            </ul>
          </div>
        </div>
        <div className="rx-footer-bottom">
          <span>© {new Date().getFullYear()} Rupee Xpert. All rights reserved.</span>
          <span>Loans subject to lender approval · Gift offer terms apply · No guaranteed sanction.</span>
        </div>
      </div>
    </footer>
  );
}

// =================================================================
// Cinematic hero
// =================================================================
function CinematicHero({ onOpenConcierge }) {
  // Fully responsive two-column hero. Real HTML on the left (headline,
  // lead, CTAs, trust icons, stats). Robot visual on the right — the
  // shared reference art cropped via CSS `object-position` so only the
  // robot portion shows. Stacks vertically on tablet / mobile.
  return (
    <section className="rx-hero rx-hero-web" data-testid="rx-hero">
      <div className="rx-hero-atmosphere" aria-hidden="true">
        <div className="rx-hero-glow rx-glow-orange" />
        <div className="rx-hero-glow rx-glow-blue" />
        <div className="rx-hero-grain" />
      </div>
      <div className="rx-hero-web-grid">
        <div className="rx-hero-web-copy is-in">
          <span className="rx-chip">
            <span className="rx-chip-dot" /> Premium Loan Concierge · India
          </span>
          <h1 className="rx-h1">
            Your Personal<br />
            <em>Gateway to<br />Better Lending.</em>
          </h1>
          <p className="rx-lead">
            One dedicated advisor. 50+ Bank &amp; NBFC Partners. A single
            conversation from application to disbursement — no filler offers,
            no customer fees.
          </p>
          <div className="rx-hero-cta">
            <a
              href="/apply"
              className="rx-btn rx-btn-primary rx-btn-lg"
              data-testid="rx-hero-cta"
            >
              Apply now — it's free <ArrowRight size={18} />
            </a>
            <a
              href="#how"
              className="rx-btn rx-btn-glass"
              data-testid="rx-hero-cta-2"
            >
              <span className="rx-play"><PlayCircle size={18} /></span>
              See how it works
            </a>
          </div>
          <div className="rx-hero-trust-grid">
            <span>
              <span className="rx-trust-ic"><ShieldCheck size={18} /></span>
              <em>No customer<br />fees, ever</em>
            </span>
            <span>
              <span className="rx-trust-ic"><PhoneCall size={18} /></span>
              <em>Talk to an expert<br />in 2 hours</em>
            </span>
            <span>
              <span className="rx-trust-ic"><Gift size={18} /></span>
              <em>Free gift on<br />disbursement</em>
            </span>
          </div>
          <div className="rx-stats rx-stats-4 is-in">
            <div>
              <strong><span className="rx-stats-cur">₹</span>1,000<em>Cr+</em></strong>
              <span>Loans Facilitated</span>
            </div>
            <div>
              <strong>50<em>+</em></strong>
              <span>Bank &amp; NBFC Partners</span>
            </div>
            <div>
              <strong>4.8<em>/5</em></strong>
              <span>Advisor Rating</span>
            </div>
            <div>
              <strong>2<em>hrs</em></strong>
              <span>First-Call SLA</span>
            </div>
          </div>
        </div>
        <div
          className="rx-hero-web-visual is-in"
          role="button"
          tabIndex={0}
          data-testid="rx-hero-concierge-panel"
          onClick={() => onOpenConcierge && onOpenConcierge("concierge")}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && onOpenConcierge) {
              e.preventDefault();
              onOpenConcierge("concierge");
            }
          }}
        >
          <div className="rx-hero-panel">
            <div className="rx-hero-panel-head">
              <span className="rx-hero-panel-dot" />
              <span className="rx-hero-panel-title">AI-Powered Loan Concierge</span>
              <span className="rx-hero-panel-live">
                <span className="rx-hero-live-dot" /> Live
              </span>
            </div>

            <div className="rx-hero-offer-card">
              <div className="rx-hero-offer-row">
                <span className="rx-hero-bank">
                  <span className="rx-hero-bank-badge">HDFC</span>
                  Personal Loan · Pre-approved
                </span>
                <span className="rx-hero-offer-tag">Best Match</span>
              </div>
              <div className="rx-hero-offer-nums">
                <div>
                  <strong>10.25<em>%</em></strong>
                  <span>Interest p.a.</span>
                </div>
                <div>
                  <strong>₹15<em>L</em></strong>
                  <span>Sanctioned</span>
                </div>
                <div>
                  <strong>48<em>hrs</em></strong>
                  <span>Disbursal</span>
                </div>
              </div>
            </div>

            <div className="rx-hero-features">
              <button
                type="button"
                className="rx-hero-feat"
                data-testid="rx-hero-feat-compare"
                onClick={(e) => { e.stopPropagation(); onOpenConcierge && onOpenConcierge("compare"); }}
              ><Search size={16} /><em>Compare Offers</em></button>
              <button
                type="button"
                className="rx-hero-feat"
                data-testid="rx-hero-feat-rates"
                onClick={(e) => { e.stopPropagation(); onOpenConcierge && onOpenConcierge("rates"); }}
              ><Percent size={16} /><em>Best Rates</em></button>
              <button
                type="button"
                className="rx-hero-feat"
                data-testid="rx-hero-feat-fast"
                onClick={(e) => { e.stopPropagation(); onOpenConcierge && onOpenConcierge("fast"); }}
              ><Zap size={16} /><em>Fast Processing</em></button>
              <button
                type="button"
                className="rx-hero-feat"
                data-testid="rx-hero-feat-advisor"
                onClick={(e) => { e.stopPropagation(); onOpenConcierge && onOpenConcierge("advisor"); }}
              ><HeartHandshake size={16} /><em>Dedicated Advisor</em></button>
            </div>

            <div className="rx-hero-chart">
              <div className="rx-hero-chart-head">
                <span>Smarter Loans · Brighter Futures</span>
                <span className="rx-hero-chart-up"><TrendingUp size={14} /> +38%</span>
              </div>
              <div className="rx-hero-chart-bars">
                <span style={{ height: "28%" }} /><span style={{ height: "44%" }} />
                <span style={{ height: "36%" }} /><span style={{ height: "62%" }} />
                <span style={{ height: "52%" }} /><span style={{ height: "78%" }} />
                <span style={{ height: "68%" }} /><span style={{ height: "92%" }} />
              </div>
            </div>

            <div className="rx-hero-panel-foot">
              <span className="rx-hero-avatars">
                <span /><span /><span /><span />
              </span>
              <span className="rx-hero-foot-text">
                <em>50+ Bank &amp; NBFC Partners</em>
                RBI-regulated · SSL-secured
              </span>
              <span className="rx-hero-foot-badge"><BadgeCheck size={16} /></span>
            </div>

            <span className="rx-hero-panel-hint">
              Tap to open · Concierge <ArrowRight size={12} />
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Loan Products — glowing tiles (no photography)
// =================================================================
function ProductTile({ p, i }) {
  return (
    <a href={`/apply?product=${p.id}`} className={`rx-tile rx-tile-${p.tone}`} data-testid={`rx-tile-${p.id}`}
       style={{ animationDelay: `${i * 70}ms` }}>
      <div className="rx-tile-glow" aria-hidden="true" />
      <div className="rx-tile-inner">
        <div className="rx-tile-icon"><p.Icon size={30} strokeWidth={1.4} /></div>
        <div className="rx-tile-rate">
          <span>from</span>
          <strong>{p.rate}<em>p.a.</em></strong>
        </div>
        <h3>{p.label}</h3>
        <span className="rx-tile-max" data-testid={`rx-tile-max-${p.id}`}>{p.maxLabel}</span>
        <span className="rx-tile-cta">Check eligibility <ArrowRight size={14} /></span>
      </div>
    </a>
  );
}

function LoanProductsSection() {
  return (
    <section id="products" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Our products</span>
          <h2 className="rx-h2">Seven flagship loans. <em>One advisor.</em></h2>
          <p className="rx-lead">
            Every tile links to the same 60-second application. We route your enquiry to the
            specific product-desk and lender panel most likely to approve you.
          </p>
        </div>
        <div className="rx-tile-grid">
          {PRODUCTS.map((p, i) => <ProductTile key={p.id} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Designed Around Your Needs — persona photo cards
// =================================================================
function PersonaCard({ p, i }) {
  return (
    <article className="rx-persona" style={{ animationDelay: `${i * 90}ms` }}>
      <div className="rx-persona-media">
        <img src={p.image} alt="" loading="lazy" />
        <div className="rx-persona-veil" />
        <div className="rx-persona-ring" aria-hidden="true" />
      </div>
      <div className="rx-persona-body">
        <h3>{p.label}</h3>
        <p>{p.blurb}</p>
        <a href="/apply" className="rx-persona-cta">
          Get pre-approved <ArrowRight size={14} />
        </a>
      </div>
    </article>
  );
}

function PersonasSection() {
  return (
    <section id="personas" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Designed around your needs</span>
          <h2 className="rx-h2">A private-office fit for <em>every profile.</em></h2>
        </div>
        <div className="rx-persona-grid">
          {PERSONAS.map((p, i) => <PersonaCard key={p.id} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Ecosystem — large glassy cards
// =================================================================
function EcosystemSection() {
  return (
    <section id="ecosystem" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Ecosystem</span>
          <h2 className="rx-h2">One platform. <em>Every touchpoint.</em></h2>
        </div>
        <div className="rx-eco-grid">
          {ECOSYSTEM.map((e, i) => (
            <div key={e.title} className="rx-eco" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="rx-eco-glow" aria-hidden="true" />
              <div className="rx-eco-icon"><e.Icon size={22} strokeWidth={1.6} /></div>
              <h3>{e.title}</h3>
              <p>{e.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// EMI Calculator — dark cinematic
// =================================================================
function fmt(n) { if (!isFinite(n)) return "0"; return Math.round(n).toLocaleString("en-IN"); }

function EMICalculator() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(11.5);
  const [tenure, setTenure] = useState(36);
  const { emi, totalInterest, totalPayable, principalPct } = useMemo(() => {
    const r = rate / 12 / 100, n = tenure;
    const e = amount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const total = e * n;
    return {
      emi: e || 0, totalInterest: (total - amount) || 0, totalPayable: total || 0,
      principalPct: total > 0 ? (amount / total) * 100 : 0,
    };
  }, [amount, rate, tenure]);

  return (
    <section id="calculator" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Featured calculator</span>
          <h2 className="rx-h2">See the number <em>before</em> you sign the number.</h2>
        </div>
        <div className="rx-calc" data-testid="rx-emi-calc">
          <div className="rx-calc-controls">
            <SliderBlock label="Loan amount" value={`₹ ${fmt(amount)}`}
              min={50000} max={4000000} step={10000} from="₹50k" to="₹40 L"
              val={amount} onChange={setAmount} testid="rx-slider-amount" />
            <SliderBlock label="Interest rate" value={`${rate.toFixed(2)}% p.a.`}
              min={8} max={24} step={0.05} from="8%" to="24%"
              val={rate} onChange={setRate} testid="rx-slider-rate" />
            <SliderBlock label="Tenure" value={`${tenure} months`}
              min={6} max={84} step={1} from="6 mo" to="84 mo"
              val={tenure} onChange={setTenure} testid="rx-slider-tenure" />
          </div>
          <div className="rx-calc-result">
            <div className="rx-donut" style={{ "--pct": `${principalPct}%` }} aria-hidden="true">
              <div className="rx-donut-inner">
                <span>Monthly EMI</span>
                <strong>₹ {fmt(emi)}</strong>
              </div>
            </div>
            <div className="rx-calc-legend">
              <div><span className="rx-dot rx-dot-orange" /> Principal <strong>₹ {fmt(amount)}</strong></div>
              <div><span className="rx-dot rx-dot-blue"   /> Interest <strong>₹ {fmt(totalInterest)}</strong></div>
              <div><span className="rx-dot rx-dot-white"  /> Total payable <strong>₹ {fmt(totalPayable)}</strong></div>
            </div>
            <a href="/apply" className="rx-btn rx-btn-primary" data-testid="rx-calc-cta">
              Apply for this loan <ArrowRight size={16} />
            </a>
            <p className="rx-mute" style={{ fontSize: 12, marginTop: 12 }}>
              Indicative only. Actual rate, processing fee and eligibility are decided by the lender.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderBlock({ label, value, min, max, step, from, to, val, onChange, testid }) {
  return (
    <div className="rx-slider-block">
      <div className="rx-slider-head"><span>{label}</span><strong>{value}</strong></div>
      <input type="range" min={min} max={max} step={step} value={val}
        onChange={(e) => onChange(+e.target.value)} aria-label={label} data-testid={testid} />
      <div className="rx-slider-scale"><span>{from}</span><span>{to}</span></div>
    </div>
  );
}

// =================================================================
// Free-Gift Billboard (CMS-driven)
// =================================================================
function GiftBillboard({ heroGift }) {
  return (
    <section className="rx-section">
      <div className="rx-container">
        <div className="rx-gift" data-testid="rx-gift-billboard">
          <div className="rx-gift-glow" aria-hidden="true" />
          <div className="rx-gift-copy">
            <span className="rx-chip"><Gift size={12} /> Exclusive offer</span>
            <h2 className="rx-h2">Free gift on <em>successful disbursement.</em></h2>
            <p>
              {heroGift?.matter ||
                "As a thank-you, eligible applicants receive a curated premium gift when their loan is successfully disbursed through a Rupee Xpert partner lender. Terms apply."}
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="/apply" className="rx-btn rx-btn-primary" data-testid="rx-billboard-cta">
                Claim your gift <ArrowRight size={16} />
              </a>
              <a href="#gifts" className="rx-btn rx-btn-glass">See available gifts</a>
            </div>
          </div>
          <div className="rx-gift-media" data-testid="rx-gift-media">
            {heroGift?.video_url ? (
              <video autoPlay muted loop playsInline poster={heroGift?.image_url || undefined}>
                <source src={heroGift.video_url} />
              </video>
            ) : heroGift?.image_url ? (
              <img src={heroGift.image_url} alt={heroGift.title || "Rupee Xpert gift"} />
            ) : (
              <>
                <div className="rx-gift-anim" aria-hidden="true" />
                <div className="rx-gift-label">Premium gift · unveiled at disbursement</div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function GiftGrid({ gifts }) {
  if (!gifts?.length) return null;
  return (
    <section id="gifts" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Featured gifts</span>
          <h2 className="rx-h2">A thank-you at the <em>finish line.</em></h2>
        </div>
        <div className="rx-gift-list" data-testid="rx-gift-list">
          {gifts.map((g) => (
            <article key={g.gift_id} className="rx-gift-item">
              <div className="rx-thumb">
                {g.image_url ? <img src={g.image_url} alt={g.title} loading="lazy" /> : <span>Preview coming soon</span>}
              </div>
              <h4>{g.title || "Rupee Xpert gift"}</h4>
              <p>{g.description || "Awarded after successful disbursement. Terms apply."}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Partner banks marquee
// =================================================================
function BankPartners() {
  const doubled = [...PARTNER_BANKS, ...PARTNER_BANKS];
  return (
    <section id="partners" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Trusted financial partners</span>
          <h2 className="rx-h2">One platform. <em>Multiple lending partners.</em></h2>
          <p className="rx-lead">
            Rupee Xpert is an authorised Direct Sales Associate for a growing network of banks
            and NBFCs. Final loan approval, interest rate and disbursement remain at the
            lender&apos;s sole discretion.
          </p>
        </div>
      </div>
      <div className="rx-marquee" aria-hidden="true">
        <div className="rx-marquee-track">
          {doubled.map((b, i) => <div key={i} className="rx-marquee-chip">{b}</div>)}
        </div>
      </div>
      <div className="rx-marquee rx-marquee-rev" aria-hidden="true">
        <div className="rx-marquee-track">
          {doubled.slice().reverse().map((b, i) => <div key={i} className="rx-marquee-chip">{b}</div>)}
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Journey
// =================================================================
function JourneySection() {
  return (
    <section id="journey" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">How it works</span>
          <h2 className="rx-h2">Six calm steps between you and <em>disbursement.</em></h2>
        </div>
        <ol className="rx-journey">
          {JOURNEY.map((s, i) => (
            <li key={s.t} style={{ animationDelay: `${i * 80}ms` }}>
              <div className="rx-journey-node"><s.Icon size={20} /></div>
              <div>
                <span className="rx-journey-num">Step {String(i + 1).padStart(2, "0")}</span>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// =================================================================
// Testimonials
// =================================================================
function Testimonials() {
  const [idx, setIdx] = useState(0);
  const total = TESTIMONIALS.length;
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6500);
    return () => clearInterval(t);
  }, [total]);
  const t = TESTIMONIALS[idx];
  const initials = t.name.replace(/\./g, "").split("").filter((c) => /[A-Z]/.test(c)).join("");
  return (
    <section className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Customer stories</span>
          <h2 className="rx-h2">Quiet outcomes. <em>Loud referrals.</em></h2>
        </div>
        <div className="rx-carousel" data-testid="rx-carousel">
          <figure key={idx} className="rx-carousel-slide">
            <div className="rx-avatar" aria-hidden="true">{initials || t.name[0]}</div>
            <div className="rx-stars" aria-hidden="true">
              {[0,1,2,3,4].map((s) => <Star key={s} size={16} fill="currentColor" strokeWidth={0} />)}
            </div>
            <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
            <figcaption><strong>{t.name}</strong><span>{t.tag}</span></figcaption>
          </figure>
          <div className="rx-carousel-controls">
            <button aria-label="Previous" onClick={() => setIdx((i) => (i - 1 + total) % total)} data-testid="rx-carousel-prev"><ChevronLeft size={18} /></button>
            <div className="rx-carousel-dots">
              {TESTIMONIALS.map((_, i) => (
                <button key={i} className={i === idx ? "is-on" : ""} aria-label={`Story ${i + 1}`} onClick={() => setIdx(i)} />
              ))}
            </div>
            <button aria-label="Next" onClick={() => setIdx((i) => (i + 1) % total)} data-testid="rx-carousel-next"><ChevronRight size={18} /></button>
          </div>
        </div>
        <p className="rx-mute rx-center" style={{ fontSize: 12, marginTop: 22 }}>
          * Illustrative customer stories. Real, consented testimonials will replace this section as they become available.
        </p>
      </div>
    </section>
  );
}

// =================================================================
// FAQ + final CTA
// =================================================================
function FAQBlock() {
  return (
    <section id="faqs" className="rx-section">
      <div className="rx-container">
        <div className="rx-section-head">
          <span className="rx-eyebrow">Frequently asked</span>
          <h2 className="rx-h2">Straight answers, <em>no fine-print theatrics.</em></h2>
        </div>
        <div className="rx-faq" data-testid="rx-faq">
          {FAQS.map((f, i) => (
            <details key={i} open={i === 0}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="rx-section" style={{ paddingTop: 40, paddingBottom: 96 }}>
      <div className="rx-container">
        <div className="rx-final" data-testid="rx-final-cta">
          <div className="rx-final-glow" aria-hidden="true" />
          <div>
            <span className="rx-eyebrow">Ready when you are</span>
            <h2 className="rx-h2" style={{ marginTop: 8 }}>Start the conversation. <em>Skip the queue.</em></h2>
            <p className="rx-lead">
              Filling the form does not commit you to a loan — it just opens a private line to
              one of our advisors. No obligation, no upfront fee.
            </p>
          </div>
          <div className="rx-final-actions">
            <a href="/apply" className="rx-btn rx-btn-primary rx-btn-lg" data-testid="rx-final-cta-btn">
              Apply now — free <ArrowRight size={18} />
            </a>
            <a href="tel:+911800000000" className="rx-btn rx-btn-glass">
              <PhoneCall size={16} /> Talk to an advisor
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// =================================================================
// Lead form
// =================================================================
const EMP_TYPES = ["Salaried", "Self-employed", "Business owner", "Professional"];

function LeadForm({ onSubmitted, sourcePage = "landing", initialLoanType = "" }) {
  const [form, setForm] = useState({
    full_name: "", mobile: "", employment_type: "",
    monthly_income: "", city: "", loan_amount: "",
    loan_type: initialLoanType || "Personal Loan",
  });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please tick the consent box to proceed.");
    if (!/^[6-9]\d{9}$/.test(form.mobile.replace(/\D/g, "").slice(-10))) return toast.error("Enter a valid 10-digit mobile number.");
    setBusy(true);
    try {
      const qp = new URLSearchParams(window.location.search);
      const payload = { ...form,
        monthly_income: Number(form.monthly_income) || 0,
        loan_amount: Number(form.loan_amount) || 0,
        consent: true, page: sourcePage,
        source: qp.get("source") || "meta-ads",
        campaign_id: qp.get("campaign_id") || qp.get("utm_campaign") || "",
        ad_id: qp.get("ad_id") || qp.get("utm_content") || "",
        adset_id: qp.get("adset_id") || qp.get("utm_source") || "",
        fbclid: qp.get("fbclid") || "",
        external_lead_id: qp.get("lead_id") || "",
      };
      const r = await http.post("/public/landing-lead", payload);
      setDone(true);
      onSubmitted && onSubmitted(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rx-success" data-testid="rx-success">
      <h3 className="rx-h3">Thanks — we&apos;ve got your details.</h3>
      <p>A Rupee Xpert advisor will call you shortly on the mobile you shared to take the conversation forward. If your loan is disbursed by a partner lender, we&apos;ll also share the free-gift redemption details.</p>
    </div>
  );
  return (
    <form className="rx-lead-form" onSubmit={submit} data-testid="rx-lead-form">
      <span className="rx-eyebrow">Apply in 60 seconds</span>
      <h3 className="rx-h3">Tell us about you.</h3>
      <p className="rx-mute" style={{ fontSize: 13, marginTop: 4 }}>
        Filling this form does not commit you to a loan — it starts a private conversation with one of our advisors.
      </p>
      <div className="rx-form-grid" style={{ marginTop: 18 }}>
        <label><span className="rx-label-text">Full Name</span>
          <input required value={form.full_name} onChange={setField("full_name")} placeholder="As per your PAN" data-testid="rx-form-name" /></label>
        <label><span className="rx-label-text">Mobile</span>
          <input required inputMode="numeric" pattern="[0-9]{10}" maxLength={10}
            value={form.mobile} onChange={setField("mobile")} placeholder="10-digit mobile" data-testid="rx-form-mobile" /></label>
        <label><span className="rx-label-text">Employment Type</span>
          <select required value={form.employment_type} onChange={setField("employment_type")} data-testid="rx-form-employment">
            <option value="">Select…</option>
            {EMP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label><span className="rx-label-text">Monthly Income (₹)</span>
          <input required type="number" min="0" value={form.monthly_income} onChange={setField("monthly_income")} placeholder="e.g. 65000" data-testid="rx-form-income" /></label>
        <label><span className="rx-label-text">City</span>
          <input required value={form.city} onChange={setField("city")} placeholder="e.g. Mumbai" data-testid="rx-form-city" /></label>
        <label><span className="rx-label-text">Required Loan Amount (₹)</span>
          <input required type="number" min="0" value={form.loan_amount} onChange={setField("loan_amount")} placeholder="e.g. 500000" data-testid="rx-form-amount" /></label>
        <label className="rx-full"><span className="rx-label-text">Loan type</span>
          <select value={form.loan_type} onChange={setField("loan_type")} data-testid="rx-form-type">
            {PRODUCTS.map((p) => <option key={p.id}>{p.label}</option>)}
          </select></label>
      </div>
      <label className="rx-consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="rx-form-consent" />
        <span>I authorise Rupee Xpert and its partner lenders to contact me by phone, SMS or email in connection with this loan enquiry, and to share my details with lending partners for eligibility evaluation. This overrides my DND/NCPR registration and is valid for 12 months.</span>
      </label>
      <button className="rx-btn rx-btn-primary" disabled={busy} data-testid="rx-form-submit">
        {busy ? "Sending…" : <>Get my best offer <ArrowRight size={16} /></>}
      </button>
      <p className="rx-mute" style={{ fontSize: 11, marginTop: 12, textAlign: "center" }}>
        No spam · No upfront fee · Details used only for loan evaluation.
      </p>
    </form>
  );
}

// =================================================================
// Data hook
// =================================================================
function useGiftOffers() {
  const [gifts, setGifts] = useState([]);
  useEffect(() => {
    http.get("/public/gift-offers")
      .then((r) => setGifts(r.data.gifts || []))
      .catch(() => setGifts([]));
  }, []);
  return gifts;
}

// =================================================================
// AI-Powered Loan Concierge modal
// - Step 1: pick a loan type (7 flagship products)
// - Step 2: mini bank/NBFC comparison for the picked product
// - CTA: continue to the existing /apply flow with product + bank context
// =================================================================
const BANK_OFFERS = {
  personal: [
    { b: "HDFC Bank",      r: "10.25%", t: "48 hrs",  tag: "Best Match" },
    { b: "ICICI Bank",     r: "10.75%", t: "72 hrs",  tag: "Lowest EMI" },
    { b: "Axis Bank",      r: "10.99%", t: "48 hrs",  tag: "" },
    { b: "Bajaj Finserv",  r: "12.49%", t: "24 hrs",  tag: "Fastest" },
  ],
  business: [
    { b: "HDFC Bank",      r: "13.99%", t: "5 days",  tag: "Best Match" },
    { b: "ICICI Bank",     r: "14.25%", t: "5 days",  tag: "" },
    { b: "Kotak Bank",     r: "14.75%", t: "3 days",  tag: "Fastest" },
    { b: "Tata Capital",   r: "15.50%", t: "3 days",  tag: "" },
  ],
  bt: [
    { b: "HDFC Bank",      r: "9.99%",  t: "10 days", tag: "Best Match" },
    { b: "ICICI Bank",     r: "10.15%", t: "10 days", tag: "" },
    { b: "Axis Bank",      r: "10.25%", t: "7 days",  tag: "Fastest" },
    { b: "L&T Finance",    r: "10.50%", t: "7 days",  tag: "" },
  ],
  home: [
    { b: "SBI",            r: "8.35%",  t: "15 days", tag: "Lowest Rate" },
    { b: "HDFC Bank",      r: "8.45%",  t: "12 days", tag: "Best Match" },
    { b: "ICICI Bank",     r: "8.55%",  t: "12 days", tag: "" },
    { b: "Bank of Baroda", r: "8.60%",  t: "18 days", tag: "" },
  ],
  lap: [
    { b: "HDFC Bank",      r: "9.25%",  t: "15 days", tag: "Best Match" },
    { b: "ICICI Bank",     r: "9.40%",  t: "15 days", tag: "" },
    { b: "Axis Bank",      r: "9.60%",  t: "12 days", tag: "Fastest" },
    { b: "Bajaj Finserv",  r: "10.00%", t: "10 days", tag: "" },
  ],
  auto: [
    { b: "HDFC Bank",      r: "9.10%",  t: "48 hrs",  tag: "Best Match" },
    { b: "ICICI Bank",     r: "9.25%",  t: "48 hrs",  tag: "" },
    { b: "Axis Bank",      r: "9.40%",  t: "48 hrs",  tag: "" },
    { b: "Kotak Bank",     r: "9.50%",  t: "24 hrs",  tag: "Fastest" },
  ],
  edu: [
    { b: "SBI",            r: "9.50%",  t: "10 days", tag: "Lowest Rate" },
    { b: "HDFC Credila",   r: "10.25%", t: "7 days",  tag: "Best Match" },
    { b: "ICICI Bank",     r: "10.50%", t: "10 days", tag: "" },
    { b: "Axis Bank",      r: "10.75%", t: "10 days", tag: "" },
  ],
};

const MODE_META = {
  concierge: { title: "AI-Powered Loan Concierge", sub: "Pick your loan and see which bank fits you best.",           key: null       },
  compare:   { title: "Compare Offers",             sub: "Side-by-side comparison across 50+ Bank & NBFC Partners.",       key: null       },
  rates:     { title: "Best Rates Today",           sub: "We only surface offers you are most likely to be approved for.", key: "lowest"   },
  fast:      { title: "Fast Processing",            sub: "Sorted by fastest disbursal timelines.",                      key: "fastest"  },
  advisor:   { title: "Talk to a Dedicated Advisor", sub: "One named advisor — first call within 2 business hours.",     key: "advisor"  },
};

function ConciergeModal({ mode, onClose }) {
  const [product, setProduct] = useState(null);
  const [name, setName]       = useState("");
  const [mobile, setMobile]   = useState("");
  const [submitting, setSubmitting] = useState(false);

  const advisorMode = mode === "advisor";
  const meta = MODE_META[mode] || MODE_META.concierge;

  useEffect(() => {
    if (!mode) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [mode, onClose]);

  if (!mode) return null;

  // Sort banks based on mode key
  const banksFor = (pid) => {
    const list = [...(BANK_OFFERS[pid] || [])];
    if (meta.key === "lowest")   list.sort((a, b) => parseFloat(a.r) - parseFloat(b.r));
    if (meta.key === "fastest")  list.sort((a, b) => (parseInt(a.t) || 0) - (parseInt(b.t) || 0));
    return list;
  };

  const submitAdvisor = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^\d{10}$/.test(mobile.trim())) {
      toast.error("Please enter your name and a valid 10-digit mobile.");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/api/public/landing-lead", {
        name: name.trim(),
        mobile: mobile.trim(),
        product: product || "personal",
        source: "concierge_advisor",
      });
      toast.success("Thanks! An advisor will call you within 2 business hours.");
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rx-cx-overlay" data-testid="rx-concierge-modal" onClick={onClose}>
      <div className="rx-cx-modal" onClick={(e) => e.stopPropagation()}>
        <button className="rx-cx-close" onClick={onClose} data-testid="rx-concierge-close" aria-label="Close">
          <ChevronLeft size={18} /> Close
        </button>
        <div className="rx-cx-head">
          <span className="rx-hero-panel-dot" />
          <h3>{meta.title}</h3>
          <p>{meta.sub}</p>
        </div>

        {/* Step 1 — always shown */}
        <div className="rx-cx-step">
          <span className="rx-cx-step-label">1 · Choose your loan</span>
          <div className="rx-cx-products">
            {PRODUCTS.map((p) => {
              const active = product === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={"rx-cx-chip" + (active ? " is-active" : "")}
                  data-testid={`rx-concierge-product-${p.id}`}
                  onClick={() => setProduct(p.id)}
                >
                  <p.Icon size={16} />
                  <span>{p.label}</span>
                  <em>From {p.rate}</em>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2 — advisor: contact form; others: bank comparison */}
        {advisorMode ? (
          <form className="rx-cx-step" onSubmit={submitAdvisor} data-testid="rx-concierge-advisor-form">
            <span className="rx-cx-step-label">2 · Get a callback in 2 hours</span>
            <div className="rx-cx-form">
              <input
                type="text" placeholder="Your name" value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="rx-concierge-name"
              />
              <input
                type="tel" placeholder="10-digit mobile" value={mobile} maxLength={10}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                data-testid="rx-concierge-mobile"
              />
              <button
                type="submit" className="rx-btn rx-btn-primary rx-btn-lg"
                disabled={submitting}
                data-testid="rx-concierge-callback-submit"
              >
                {submitting ? "Requesting…" : "Request callback"} <ArrowRight size={16} />
              </button>
            </div>
          </form>
        ) : product ? (
          <div className="rx-cx-step">
            <span className="rx-cx-step-label">2 · Compare bank / NBFC offers</span>
            <div className="rx-cx-offers">
              {banksFor(product).map((b, i) => (
                <div key={i} className={"rx-cx-offer" + (i === 0 ? " is-best" : "")}>
                  <div className="rx-cx-offer-bank">
                    <span className="rx-hero-bank-badge">{b.b.split(" ")[0].toUpperCase().slice(0,4)}</span>
                    <div>
                      <strong>{b.b}</strong>
                      {b.tag && <em>{b.tag}</em>}
                    </div>
                  </div>
                  <div className="rx-cx-offer-nums">
                    <span><strong>{b.r}</strong><em>Interest p.a.</em></span>
                    <span><strong>{b.t}</strong><em>Disbursal</em></span>
                  </div>
                  <a
                    href={`/apply?product=${product}&ref=concierge&bank=${encodeURIComponent(b.b)}`}
                    className="rx-btn rx-btn-primary"
                    data-testid={`rx-concierge-apply-${product}-${i}`}
                  >
                    Apply <ArrowRight size={14} />
                  </a>
                </div>
              ))}
            </div>
            <a
              href={`/apply?product=${product}&ref=concierge`}
              className="rx-btn rx-btn-glass rx-cx-explore"
              data-testid="rx-concierge-explore-all"
            >
              Explore all offers &amp; continue application <ArrowRight size={16} />
            </a>
          </div>
        ) : (
          <div className="rx-cx-step rx-cx-hint">
            <ChevronRight size={16} /> Select a loan above to see bank &amp; NBFC offers.
          </div>
        )}
      </div>
    </div>
  );
}

// =================================================================
// Homepage
// =================================================================
export function Website() {
  const gifts = useGiftOffers();
  const heroGift = gifts.find((g) => g.video_url || g.image_url) || gifts[0];
  const [conciergeMode, setConciergeMode] = useState(null);
  return (
    <div className="rx-marketing" data-testid="rx-website">
      <Header />
      <CinematicHero onOpenConcierge={setConciergeMode} />
      <ConciergeModal mode={conciergeMode} onClose={() => setConciergeMode(null)} />
      <LoanProductsSection />
      <EMICalculator />
      <PersonasSection />
      <EcosystemSection />
      <GiftBillboard heroGift={heroGift} />
      <BankPartners />
      <JourneySection />
      <Testimonials />
      <GiftGrid gifts={gifts} />
      <FAQBlock />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// =================================================================
// Landing (`/apply`)
// =================================================================
export function Landing() {
  const gifts = useGiftOffers();
  const heroGift = gifts.find((g) => g.video_url || g.image_url) || gifts[0];
  // Pick up the product the visitor clicked on (?product=personal|business|…).
  const productId = (typeof window !== "undefined")
    ? new URLSearchParams(window.location.search).get("product")
    : null;
  const selectedProduct = PRODUCTS.find((p) => p.id === productId);
  const heroMax = selectedProduct ? selectedProduct.maxLabel : null;
  const heroProductLabel = selectedProduct ? selectedProduct.label : null;
  return (
    <div className="rx-marketing" data-testid="rx-landing">
      <Header compact />
      <section className="rx-hero rx-hero-landing">
        <div className="rx-hero-atmosphere" aria-hidden="true">
          <img src={IMG.heroFigure} alt="" className="rx-hero-figure" fetchpriority="high" />
          <div className="rx-hero-glow rx-glow-orange" />
          <div className="rx-hero-glow rx-glow-blue" />
          <div className="rx-hero-grain" />
        </div>
        <div className="rx-container">
          <div className="rx-landing-hero">
            <div className="rx-hero-copy is-in">
              <span className="rx-chip"><span className="rx-chip-dot" /> Limited-time offer</span>
              {selectedProduct ? (
                <h1 className="rx-h1" data-testid="rx-landing-hero-headline">
                  {selectedProduct.label}.<br /><em>{heroMax}.</em>
                </h1>
              ) : (
                <h1 className="rx-h1" data-testid="rx-landing-hero-headline">
                  Loans, curated.<br /><em>Plus a gift on us.</em>
                </h1>
              )}
              <p className="rx-lead">
                {heroProductLabel
                  ? `Get a ${heroProductLabel.toLowerCase()} offer tailored to your profile, ${heroMax}. `
                  : "A tailored loan offer from 50+ Bank & NBFC Partners. "}
                A free premium gift for every eligible customer whose loan is successfully disbursed
                through a Rupee Xpert partner. No obligation — start with a quick chat.
              </p>
              <div className="rx-hero-cta">
                <a href="#form" className="rx-btn rx-btn-primary rx-btn-lg">Apply now <ArrowRight size={18} /></a>
                <a href="#how" className="rx-btn rx-btn-glass">See how it works</a>
              </div>
              <div className="rx-hero-trust">
                <span><ShieldCheck size={14} /> No customer fees</span>
                <span><PhoneCall size={14} /> Call in 2 hours</span>
                <span><Wallet size={14} /> Free gift on disbursement</span>
              </div>
            </div>
            <div id="form">
              <LeadForm sourcePage="landing" initialLoanType={selectedProduct?.label} />
            </div>
          </div>
        </div>
      </section>
      <GiftBillboard heroGift={heroGift} />
      <div id="how"><JourneySection /></div>
      <BankPartners />
      <GiftGrid gifts={gifts} />
      <FAQBlock />
      <Footer />
    </div>
  );
}

// =================================================================
// Legal pages
// =================================================================
const LEGAL_CONTENT = {
  privacy: { title: "Privacy Policy", eyebrow: "Data · Consent · Retention", body: [
    ["What we collect", "When you submit an enquiry we collect your name, mobile number, email, city, employment type, monthly income and required loan amount, along with basic technical metadata (referring campaign, IP, device) needed to route the enquiry."],
    ["Why we collect it", "Solely to evaluate loan eligibility with our partner banks and NBFCs, to allow one of our advisors to contact you, and to comply with our regulatory record-keeping obligations."],
    ["Who we share it with", "Details are shared only with the specific lender(s) whose product you are being evaluated for. Rupee Xpert does not sell, rent or trade personal information."],
    ["How long we keep it", "Enquiry records are retained for up to 24 months from your last interaction, after which they are anonymised or deleted, except where a longer period is required by law."],
    ["Your rights", "You can request a copy, correction or deletion of your data at any time by writing to hello@rupee-xpert.com. We will action valid requests within 30 days."],
    ["Contact", "For any privacy question or grievance, please write to our Data Protection contact at hello@rupee-xpert.com."],
  ]},
  terms: { title: "Terms & Conditions", eyebrow: "Service · Eligibility · Liability", body: [
    ["Nature of service", "Rupee Xpert is a channel partner and authorised Direct Sales Associate for regulated Indian banks and NBFCs. We facilitate loan applications; we do not lend or underwrite on our own books."],
    ["Eligibility", "The service is intended for Indian residents aged 21 or above. Final eligibility, interest rate, processing fee, tenure and disbursement decisions are taken solely by the lender."],
    ["No guarantee of sanction", "Submission of an enquiry does not guarantee sanction of a loan. Rupee Xpert makes no representation about approval, rate or amount."],
    ["No customer fees", "Rupee Xpert does not charge customers any fee for its advisory service. Any processing fee, stamp duty or other charge levied by the lender will be disclosed on the sanction letter."],
    ["Communication consent", "By submitting the form you authorise Rupee Xpert and its partner lenders to contact you via phone, SMS, email or WhatsApp in connection with your enquiry, overriding any DND / NCPR registration for a period of 12 months."],
    ["Limitation of liability", "Rupee Xpert's aggregate liability under these Terms is limited to the fee it earned from the specific transaction that gave rise to the claim."],
    ["Governing law", "These Terms are governed by the laws of India. Courts at Mumbai will have exclusive jurisdiction."],
  ]},
  disclosures: { title: "Fair-Practice & Regulatory Disclosures", eyebrow: "Transparency · Compliance", body: [
    ["Role & remuneration", "Rupee Xpert acts as an authorised Direct Sales Associate for its partner banks and NBFCs. We are paid a sourcing / distribution fee by the lender on successful disbursement. We do not accept fees from customers."],
    ["Fair Practice Code", "We follow the Fair Practice Code prescribed by the Reserve Bank of India for DSAs — including no misleading representation, no coercion, and full disclosure of applicable fees on the sanction letter."],
    ["Grievance redressal", "Any complaint may be raised at hello@rupee-xpert.com. We aim to acknowledge within 3 working days and resolve within 30 days. If unresolved, you may escalate to the concerned lender's Nodal Officer or to the RBI Ombudsman."],
    ["Data & KYC", "KYC verification is performed by the lender at the time of sanction. Rupee Xpert only collects information necessary to route your enquiry."],
    ["Advertising", "All rate, tenure and gift claims on this website are indicative. Actual figures depend on the lender's policy, your profile and product availability at the time of disbursement."],
  ]},
};

export function LegalPage({ slug }) {
  const doc = LEGAL_CONTENT[slug] || LEGAL_CONTENT.privacy;
  return (
    <div className="rx-marketing" data-testid={`rx-legal-${slug}`}>
      <Header />
      <section className="rx-section" style={{ paddingTop: 80 }}>
        <div className="rx-container rx-legal">
          <a href="/website" className="rx-legal-back"><ChevronLeft size={14} /> Back to home</a>
          <span className="rx-eyebrow">{doc.eyebrow}</span>
          <h1 className="rx-h1" style={{ fontSize: "clamp(32px,4.4vw,54px)", marginTop: 8 }}>{doc.title}</h1>
          <p className="rx-lead">Last updated: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}.</p>
          <div className="rx-legal-body">
            {doc.body.map(([h, p]) => (
              <section key={h}><h2 className="rx-h3">{h}</h2><p>{p}</p></section>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
